/**
 * MembersSection: who has access to the active team. Owner-only actions
 * (invite, transfer, remove) are gated by `current_user_is_owner` from the
 * API, which refuses them for anyone else (TRA-1626 made the invite
 * owner-only); leaving is anyone's. Under staff impersonation the list is
 * the impersonated team's and every action is hidden, since the API
 * refuses impersonated writes.
 */

import { useState } from 'react'
import { UserPlus } from 'lucide-react'
import { toast } from 'sonner'

import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { SettingsList } from '@/components/settings/SettingsList'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsSection } from '@/components/settings/SettingsSection'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { useTeam } from '@/contexts/TeamContext'
import {
  useInviteMember,
  useRemoveMember,
  useTeamMembers,
  useTransferOwnership,
  type TeamMember,
} from '@/hooks/useTeamSettings'
import { countLabel } from '@/lib/utils'

import { InviteMemberDialog } from './InviteMemberDialog'
import { MemberRow } from './MemberRow'

interface MemberRef {
  id: string
  displayName: string
}

function memberRef(member: TeamMember): MemberRef {
  const displayName =
    [member.first_name, member.last_name].filter(Boolean).join(' ') ||
    member.username ||
    member.email
  return { id: member.id, displayName }
}

function memberCountLabel(count: number, currentUserIsOwner: boolean): string {
  const members = countLabel(count, 'member')
  return currentUserIsOwner ? `${members} · you are the owner` : members
}

function inviteErrorMessage(error: unknown): string {
  if (getApiErrorStatus(error) === 404) {
    return 'No user found with that email. They must sign up first.'
  }
  return getApiErrorDetail(error) ?? 'Could not invite that member.'
}

export function MembersSection({ section }: SettingsSectionProps): React.JSX.Element {
  const { activeTeam, activeTeamId, teams, switchTeam, impersonatingTeamId } = useTeam()
  const { user, signinSilent } = useAuth()
  const currentUserId = user?.profile.sub ?? null
  const readOnly = impersonatingTeamId !== null

  const membersQuery = useTeamMembers()
  const inviteMutation = useInviteMember()
  const removeMutation = useRemoveMember()
  const transferMutation = useTransferOwnership()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<MemberRef | null>(null)
  const [transferTarget, setTransferTarget] = useState<MemberRef | null>(null)
  const [leaving, setLeaving] = useState<MemberRef | null>(null)

  const members = membersQuery.data?.members ?? []
  const currentUserIsOwner = membersQuery.data?.current_user_is_owner ?? false
  const busy = removeMutation.isPending || transferMutation.isPending

  const invite = (email: string): void => {
    setInviteError(null)
    inviteMutation.mutate(
      { data: { email } },
      {
        onSuccess: () => {
          toast.success(`Invited ${email}`)
          setInviteOpen(false)
        },
        onError: (error) => {
          setInviteError(inviteErrorMessage(error))
        },
      },
    )
  }

  const leave = (self: MemberRef): void => {
    removeMutation.mutate(
      { memberId: self.id },
      {
        onSuccess: async () => {
          toast.success('You left the team')
          setLeaving(null)
          // Refresh the JWT so the groups claim drops this team, then move
          // to another team if one remains. With zero teams left a full
          // reload is required: ensure-team only runs during app bootstrap,
          // so reloading is what provisions the fallback personal team.
          await signinSilent()
          const next = teams.find((t) => t.id !== activeTeamId)
          if (next) {
            switchTeam(next.id) // reloads the page
          } else {
            window.location.reload()
          }
        },
        onError: (error) => {
          toast.error(getApiErrorDetail(error) ?? 'Could not leave the team.')
        },
      },
    )
  }

  const transfer = (target: MemberRef): void => {
    transferMutation.mutate(
      { memberId: target.id },
      {
        onSuccess: () => {
          toast.success('Ownership transferred')
          setTransferTarget(null)
        },
        onError: (error) => {
          toast.error(getApiErrorDetail(error) ?? 'Could not transfer ownership.')
        },
      },
    )
  }

  const remove = (target: MemberRef): void => {
    removeMutation.mutate(
      { memberId: target.id },
      {
        onSuccess: () => {
          setMemberToRemove(null)
        },
        onError: (error) => {
          toast.error(getApiErrorDetail(error) ?? 'Could not remove that member.')
        },
      },
    )
  }

  return (
    <div>
      <SettingsPageHeader
        section={section}
        action={
          readOnly || !currentUserIsOwner ? undefined : (
            <Button
              size="sm"
              onClick={() => {
                setInviteError(null)
                setInviteOpen(true)
              }}
            >
              <UserPlus />
              Invite member
            </Button>
          )
        }
      />

      <SettingsSection
        title="Members"
        description="A team has exactly one owner. Only the owner can invite members, transfer ownership or remove members. Domain members join by email domain and cannot be removed."
      >
        {membersQuery.isLoading ? (
          <Skeleton className="h-14 w-full" />
        ) : membersQuery.isError ? (
          <p className="text-sm text-destructive" role="alert">
            Failed to load members
          </p>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted-foreground">No members found</p>
        ) : (
          <>
            <SettingsList>
              {members.map((member) => {
                const ref = memberRef(member)
                return (
                  <MemberRow
                    key={member.id}
                    member={member}
                    displayName={ref.displayName}
                    isSelf={member.id === currentUserId}
                    currentUserIsOwner={currentUserIsOwner}
                    readOnly={readOnly}
                    busy={busy}
                    onTransfer={() => {
                      setTransferTarget(ref)
                    }}
                    onRemove={() => {
                      setMemberToRemove(ref)
                    }}
                    onLeave={() => {
                      setLeaving(ref)
                    }}
                  />
                )
              })}
            </SettingsList>
            <p className="mt-3 text-xs text-muted-foreground">
              {memberCountLabel(members.length, currentUserIsOwner)}
            </p>
          </>
        )}
      </SettingsSection>

      {inviteOpen && (
        <InviteMemberDialog
          isPending={inviteMutation.isPending}
          error={inviteError}
          onSubmit={invite}
          onClose={() => {
            setInviteOpen(false)
          }}
        />
      )}

      <ConfirmDialog
        open={leaving !== null}
        onOpenChange={(open) => {
          if (!open) setLeaving(null)
        }}
        title="Leave team"
        description={
          <>
            Leave <strong>{activeTeam?.name}</strong>? You lose access to its resources until
            someone invites you back.
          </>
        }
        confirmLabel="Leave team"
        pendingLabel="Leaving..."
        destructive
        isPending={removeMutation.isPending}
        onConfirm={() => {
          if (leaving) leave(leaving)
        }}
      />

      <ConfirmDialog
        open={transferTarget !== null}
        onOpenChange={(open) => {
          if (!open) setTransferTarget(null)
        }}
        title="Transfer ownership"
        description={
          <>
            Make <strong>{transferTarget?.displayName}</strong> the owner of this team? A team
            has exactly one owner, so you lose that role. They can transfer it back later.
          </>
        }
        confirmLabel="Transfer"
        pendingLabel="Transferring..."
        isPending={transferMutation.isPending}
        onConfirm={() => {
          if (transferTarget) transfer(transferTarget)
        }}
      />

      <ConfirmDialog
        open={memberToRemove !== null}
        onOpenChange={(open) => {
          if (!open) setMemberToRemove(null)
        }}
        title="Remove member"
        description={
          <>
            Remove <strong>{memberToRemove?.displayName}</strong> from this team? They lose
            access to all team resources.
          </>
        }
        confirmLabel="Remove"
        pendingLabel="Removing..."
        destructive
        isPending={removeMutation.isPending}
        onConfirm={() => {
          if (memberToRemove) remove(memberToRemove)
        }}
      />
    </div>
  )
}
